import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { PayoutRequestRow, TransactionRow } from '../../api/types';
import { TransactionDetailModal } from '../../components/transactions/TransactionDetailModal';
import { useAuth } from '../../context/AuthContext';
import { canExecutePayout, canExportTransactions } from '../../utils/roles';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function txStatusBadge(status: string): string {
  if (status === 'confirmed') return 'badge badge-success';
  if (status === 'failed' || status === 'rejected') return 'badge badge-blocked';
  return 'badge badge-pending';
}

export function TransactionsSection() {
  const { role } = useAuth();
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [payouts, setPayouts] = useState<PayoutRequestRow[]>([]);
  const [selectedTx, setSelectedTx] = useState<TransactionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executingId, setExecutingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [txRes, payoutRes] = await Promise.all([
        api.listTransactions(100),
        api.listPayoutRequests(),
      ]);
      setTransactions(txRes.transactions);
      setPayouts(payoutRes.payout_requests);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const executePayout = async (id: string) => {
    setExecutingId(id);
    try {
      await api.executePayout(id);
      setPayouts((list) =>
        list.map((p) => (p.id === id ? { ...p, status: 'executed' as const } : p)),
      );
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setExecutingId(null);
    }
  };

  const ceoCanPay = canExecutePayout(role);
  const canExport = canExportTransactions(role);
  const [exporting, setExporting] = useState(false);

  const exportAllCsv = async () => {
    setExporting(true);
    try {
      const blob = await api.exportTransactionsCsv({ limit: 5000 });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onyx-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="section-header console-header">
        <div>
          <h1 className="console-title">Transactions</h1>
          <p className="console-sub">On-chain registry &amp; bank payout queue</p>
        </div>
        {canExport ? (
          <button
            type="button"
            className="btn-outline btn-sm"
            disabled={loading || exporting}
            onClick={() => void exportAllCsv()}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        ) : null}
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <div className="section-title">Payout Requests</div>
      <div className="table-wrap" style={{ marginBottom: 24 }}>
        <div className="table-head data-table-head payouts-head">
          <div className="th">Merchant</div>
          <div className="th">Amount</div>
          <div className="th">IBAN</div>
          <div className="th">Status</div>
          <div className="th col-actions">Action</div>
        </div>
        {loading ? (
          <div className="table-row">
            <div className="col-span-all">Loading…</div>
          </div>
        ) : payouts.length === 0 ? (
          <div className="table-row">
            <div className="col-span-all">No pending payout requests</div>
          </div>
        ) : (
          payouts.map((p) => (
            <div key={p.id} className="table-row data-table-row payouts-row">
              <div>{p.merchant_name || p.merchant_id.slice(0, 8)}</div>
              <div>
                {p.amount_display} {p.currency}
              </div>
              <div className="cell-mono">{p.bank_iban || '—'}</div>
              <div>
                <span className={`badge ${p.status === 'executed' ? 'badge-success' : 'badge-pending'}`}>
                  {p.status}
                </span>
              </div>
              <div className="col-actions">
                <button
                  type="button"
                  className="btn-payout"
                  disabled={!ceoCanPay || p.status !== 'pending' || executingId === p.id}
                  title={!ceoCanPay ? 'CEO only' : undefined}
                  onClick={() => void executePayout(p.id)}
                >
                  {executingId === p.id ? 'Processing…' : 'Execute Payout via Bank'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="section-title">Transaction registry</div>
      <div className="table-wrap">
        <div className="table-head data-table-head tx-head tx-head-ledger">
          <div className="th col-id">Signature</div>
          <div className="th col-time">Time</div>
          <div className="th col-merchant">Merchant</div>
          <div className="th col-gross">Gross</div>
          <div className="th col-net">Net</div>
          <div className="th col-subtype">Sub-type</div>
          <div className="th col-status">Status</div>
        </div>
        {loading ? (
          <div className="table-row">
            <div className="col-span-all">Loading…</div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="table-row">
            <div className="col-span-all">No transactions</div>
          </div>
        ) : (
          transactions.map((tx) => (
            <div
              key={tx.id}
              className="table-row data-table-row tx-row-clickable"
              onClick={() => setSelectedTx(tx)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setSelectedTx(tx);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="col-id cell-mono">{tx.signature.slice(0, 12)}…</div>
              <div className="col-time">{formatDate(tx.created_at)}</div>
              <div className="col-merchant cell-muted">
                {tx.merchant_name || tx.merchant_id?.slice(0, 8) || '—'}
              </div>
              <div className="col-gross">
                {tx.gross_amount ?? tx.amount_display} {tx.currency}
              </div>
              <div className="col-net">
                {tx.net_amount ?? tx.amount_display} {tx.currency}
              </div>
              <div className="col-subtype cell-muted">{tx.payment_sub_type || '—'}</div>
              <div className="col-status">
                <span className={txStatusBadge(tx.status)}>{tx.status}</span>
              </div>
            </div>
          ))
        )}
      </div>

      <TransactionDetailModal
        transaction={selectedTx}
        open={Boolean(selectedTx)}
        onClose={() => setSelectedTx(null)}
      />
    </>
  );
}
